import { useEffect, useRef, useState, useCallback } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { Spinner } from '~/ui';
import { ChatMessage } from '../ChatMessage/ChatMessage';
import { Modal } from '~/components/Shared/Modal/Modal';
import SendIcon from '~/assets/svg/newSvg/send.svg?react';
import styles from './ChatWindow.module.scss';

interface MessageItem {
  id: number;
  description: string;
  ticket_id: number;
  ticket_name: string | null;
  ticket_created_at: string | null;
  user_id: number;
  my_comment: boolean;
  created_at: string;
  time: string;
  images: { id: number; full_photo_url: string }[];
  videos: { id: number; full_video_url: string; full_preview_url: string }[];
}

interface TicketInfo {
  id: number;
  name: string;
  description: string;
  review: string | null;
  created_at: string;
  ticket_photo: { id: number; full_photo_url: string }[];
  ticket_video: { id: number; full_video_url: string; full_preview_url: string }[];
  ticket_review_photo: { id: number; full_photo_url: string }[];
  ticket_review_video: { id: number; full_video_url: string; full_preview_url: string }[];
  category: { id: number; category: string } | null;
  statuses: { id: number; name: string } | null;
}

interface ChatWindowProps {
  selectedTicketId: number | null;
  search?: string;
  categoryId?: number | null;
}

export const ChatWindow = ({ selectedTicketId, search, categoryId }: ChatWindowProps) => {
  const { userID } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const toastError = useToastError();
  const queryClient = useQueryClient();
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const isInitialLoad = useRef(true);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(search ?? '');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search ?? ''), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchMessages = async ({ pageParam }: { pageParam?: number }) => {
    const params: Record<string, unknown> = { perPage: 30 };
    if (selectedTicketId) params.ticket_id = selectedTicketId;
    if (pageParam) params.before_id = pageParam;
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    if (categoryId) params.category_tickets_id = categoryId;

    const response = await instance.get(
      `admin/users/${userID}/chat/messages`,
      { params }
    );
    return response.data;
  };

  const {
    data,
    isLoading,
    isFetchingPreviousPage,
    fetchPreviousPage,
    hasPreviousPage,
  } = useInfiniteQuery({
    queryFn: fetchMessages,
    queryKey: ['user-chat-messages', userID, selectedTicketId, debouncedSearch, categoryId],
    getNextPageParam: () => undefined,
    getPreviousPageParam: (firstPage: { messages: { data: MessageItem[]; has_more: boolean; next_cursor: number | null }; ticket_info?: TicketInfo }) => {
      if (!firstPage.messages.has_more || !firstPage.messages.next_cursor) return undefined;
      return firstPage.messages.next_cursor;
    },
    refetchOnWindowFocus: false,
    refetchInterval: 30000,
  });

  const { mutate: sendComment } = useMutation(
    async ({ description, ticket_id }: { description: string; ticket_id: number }) => {
      const response = await instance.postForm('admin/ticket-comment', {
        description,
        ticket_id,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        setMessageText('');
        setIsSending(false);
        queryClient.invalidateQueries(['user-chat-messages', userID, selectedTicketId]);
        // Скролл вниз после отправки
        setTimeout(() => {
          const el = messagesRef.current;
          if (el) el.scrollTop = el.scrollHeight;
        }, 300);
      },
      onError: error => {
        setIsSending(false);
        toastError(error);
      },
    }
  );

  const handleSendMessage = () => {
    if (!messageText.trim() || !selectedTicketId || isSending) return;
    setIsSending(true);
    sendComment({ description: messageText.trim(), ticket_id: selectedTicketId });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Скролл вниз при первой загрузке или смене заявки
  useEffect(() => {
    if (data && isInitialLoad.current) {
      const el = messagesRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
      isInitialLoad.current = false;
    }
  }, [data]);

  // Сброс при смене заявки
  useEffect(() => {
    isInitialLoad.current = true;
  }, [selectedTicketId]);

  // IntersectionObserver для подгрузки старых сообщений (скролл вверх)
  useEffect(() => {
    if (isFetchingPreviousPage || !hasPreviousPage) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          const el = messagesRef.current;
          const prevHeight = el?.scrollHeight || 0;

          fetchPreviousPage().then(() => {
            requestAnimationFrame(() => {
              if (el) {
                el.scrollTop = el.scrollHeight - prevHeight;
              }
            });
          });
        }
      },
      { threshold: 0.1 }
    );

    const el = loadMoreRef.current;
    if (el) observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
    };
  }, [isFetchingPreviousPage, hasPreviousPage, fetchPreviousPage]);

  // Все сообщения из всех страниц
  const allMessages: MessageItem[] =
    data?.pages.flatMap(page => page.messages.data) ?? [];

  // Информация о заявке (из первой страницы)
  const ticketInfo: TicketInfo | undefined =
    data?.pages[data.pages.length - 1]?.ticket_info;

  // Группировка сообщений по дням
  const getDateKey = (dateStr: string) => {
    const d = new Date(dateStr);
    const locale = i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'zh' ? 'zh-CN' : i18n.language === 'en' ? 'en-US' : 'ru-RU';
    return d.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTicketTitle = (ticketId: number, createdAt: string | null) => {
    if (createdAt) {
      return `#${ticketId} ${t('request_dated')} ${dayjs(createdAt).format('DD.MM.YYYY')}`;
    }
    return t('chat_request_number', { id: ticketId });
  };

  const renderMessages = useCallback(() => {
    if (allMessages.length === 0) {
      return <div className={styles.empty}>{t('no_messages')}</div>;
    }

    const elements: JSX.Element[] = [];
    let lastDate = '';
    let lastTicketId: number | null = null;

    allMessages.forEach(msg => {
      const dateKey = getDateKey(msg.created_at);

      if (selectedTicketId === null && msg.ticket_id !== lastTicketId) {
        elements.push(
          <div key={`ticket-sep-${msg.ticket_id}-${msg.id}`} className={styles.ticketSeparator}>
            <span
              className={styles.ticketSeparatorLink}
              onClick={() => navigate(`/requests/${msg.ticket_id}`)}
            >
              {formatTicketTitle(msg.ticket_id, msg.ticket_created_at)}
            </span>
          </div>
        );
        lastTicketId = msg.ticket_id;
        lastDate = '';
      }

      if (dateKey !== lastDate) {
        elements.push(
          <div key={`date-${dateKey}-${msg.id}`} className={styles.dateSeparator}>
            {dateKey}
          </div>
        );
        lastDate = dateKey;
      }

      elements.push(
        <ChatMessage
          key={msg.id}
          description={msg.description}
          isMyComment={msg.my_comment}
          time={msg.time}
          images={msg.images}
          videos={msg.videos}
        />
      );
    });

    return elements;
  }, [allMessages, selectedTicketId, navigate, t, i18n.language]);

  const headerTitle = selectedTicketId
    ? formatTicketTitle(selectedTicketId, ticketInfo?.created_at ?? null)
    : t('all_requests');

  return (
    <div className={styles.container}>
      {/* Шапка */}
      <div className={styles.header}>
        {selectedTicketId ? (
          <span
            className={styles.headerLink}
            onClick={() => navigate(`/requests/${selectedTicketId}`)}
          >
            {headerTitle}
          </span>
        ) : (
          <span className={styles.headerTitle}>{headerTitle}</span>
        )}
      </div>

      {/* Сообщения */}
      <div className={styles.messages} ref={messagesRef}>
        {/* Триггер подгрузки старых сообщений */}
        <div ref={loadMoreRef} className={styles.loadMoreTrigger} />
        {isFetchingPreviousPage && (
          <div className={styles.loader}>
            <Spinner />
          </div>
        )}

        {isLoading ? (
          <div className={styles.loader}>
            <Spinner />
          </div>
        ) : (
          <>
            {selectedTicketId && ticketInfo && (
              <div className={styles.ticketInfo}>
                <div className={styles.ticketInfoTitle}>{formatTicketTitle(ticketInfo.id, ticketInfo.created_at)}</div>
                {ticketInfo.description && (
                  <div className={styles.ticketInfoDesc}>
                    {ticketInfo.description}
                  </div>
                )}
                <div className={styles.ticketInfoMeta}>
                  {ticketInfo.category && (
                    <span className={styles.ticketInfoTag}>
                      {ticketInfo.category.category}
                    </span>
                  )}
                  {ticketInfo.statuses && (
                    <span className={styles.ticketInfoTag}>
                      {ticketInfo.statuses.name}
                    </span>
                  )}
                </div>
                {ticketInfo.ticket_photo?.length > 0 && (
                  <div className={styles.ticketInfoImages}>
                    {ticketInfo.ticket_photo.map((photo: { id: number; full_photo_url: string }) => (
                      <img
                        key={photo.id}
                        src={photo.full_photo_url}
                        alt=""
                        className={styles.ticketInfoImage}
                        onClick={() => setPreviewImage(photo.full_photo_url)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {renderMessages()}

            {selectedTicketId && ticketInfo?.review && (
              <div className={styles.reviewBlock}>
                <div className={styles.reviewTitle}>{t('chat_review')}</div>
                <div className={styles.reviewText}>{ticketInfo.review}</div>
                {ticketInfo.ticket_review_photo?.length > 0 && (
                  <div className={styles.reviewImages}>
                    {ticketInfo.ticket_review_photo.map((photo: { id: number; full_photo_url: string }) => (
                      <img
                        key={photo.id}
                        src={photo.full_photo_url}
                        alt=""
                        className={styles.reviewImage}
                        onClick={() => setPreviewImage(photo.full_photo_url)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Форма отправки сообщения */}
      {selectedTicketId ? (
        <div className={styles.inputBar}>
          <textarea
            className={styles.inputField}
            placeholder={t('type_message')}
            value={messageText}
            onChange={e => setMessageText(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
          />
          <button
            className={styles.sendButton}
            onClick={handleSendMessage}
            disabled={!messageText.trim() || isSending}
          >
            <SendIcon />
          </button>
        </div>
      ) : (
        <div className={styles.inputBarDisabled}>
          {t('select_request_to_send')}
        </div>
      )}

      {previewImage && (
        <Modal isOpen={true} onClose={() => setPreviewImage(null)}>
          <img
            src={previewImage}
            alt=""
            style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain' }}
          />
        </Modal>
      )}
    </div>
  );
};
